import { type IPublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { FrameNotFound } from '@Components/errorHandling';
import { LOGIN_REQUEST } from '@Constants/authorizationConstants';
import { UserText } from '@Constants/UserText';
import { BrandingProvider, type BrandTheme } from '@Contexts/branding';
import { useCustomDomain } from '@Contexts/customDomain/CustomDomainContext';
import { SessionProvider, useSession } from '@Contexts/session';
import { useStoredToken } from '@Hooks/useStoredToken';
import {
    createBrandTheme,
    type ErrorComponentProviderProps,
    type LoginProviderProps,
    SynqoFrontendProvider,
} from '@synqo/frontend-component-library';
import { acquireToken, useMsalNavigation } from '@synqo/cloud-client-shared';
import { type FC, useCallback, useMemo, useState } from 'react';
import { Outlet } from 'react-router';
import { SynqoCloudHttpConfigProvider } from './gui/providers';
import { CustomDomainNotFound } from './gui/login/CustomDomainNotFound';

type AppWrapperProps = {
    pca: IPublicClientApplication;
};

const translation = { applicationName: UserText.APP_NAME };

export const AppWrapper: FC<AppWrapperProps> = ({ pca }: AppWrapperProps) => {
    const { removeToken } = useStoredToken();
    const { update } = useSession();
    const [theme, setTheme] = useState<BrandTheme>(createBrandTheme);
    const customDomain = useCustomDomain();

    useMsalNavigation(pca);

    const getTokenFromMsal = useCallback(() => acquireToken(pca, LOGIN_REQUEST), [pca]);

    const loginProviderConfig: LoginProviderProps = useMemo(() => {
        return {
            onLogout: () => {
                removeToken();
                update(null);
            },
            tokenProvider: getTokenFromMsal,
        };
    }, [getTokenFromMsal, removeToken, update]);

    const errorComponentConfig: ErrorComponentProviderProps = useMemo(() => {
        return {
            notFoundComponent: FrameNotFound,
        };
    }, []);

    return (
        <MsalProvider instance={pca}>
            <SynqoFrontendProvider
                login={loginProviderConfig}
                translation={translation}
                errorComponent={errorComponentConfig}
                theme={theme}
            >
                <SynqoCloudHttpConfigProvider>
                    <SessionProvider>
                        <BrandingProvider onThemeChange={setTheme}>
                            {customDomain.kind === 'notFound' ? <CustomDomainNotFound /> : <Outlet />}
                        </BrandingProvider>
                    </SessionProvider>
                </SynqoCloudHttpConfigProvider>
            </SynqoFrontendProvider>
        </MsalProvider>
    );
};
